/**
 * Same-tab sign-in for the Client Portal.
 *
 * The portal sends the whole tab here with `?type=<provider>&returnUrl=<portal callback>&state=<nonce>`.
 * This page signs the user in with the provider, swaps the provider token for a single-use code at the
 * Client Portal API, and sends the tab back to the portal with `?code=...&state=...` (or `?error=...`).
 * The return address is only ever the canonical one the API confirmed, never the raw query value.
 */

export type Provider = 'Microsoft' | 'Google';

export type SignInErrorKind = 'cancelled' | 'failed';

export interface IRedirectSignInRequest {
  type: Provider;
  returnUrl: string;
  state: string;
  /** Pre-selects the account at Microsoft, e.g. the Teams user's sign-in name. Never trusted as identity. */
  loginHint?: string;
}

/** What survives the round trip to the provider, kept in this tab's sessionStorage. */
export interface IPendingRedirectSignIn {
  type: Provider;
  /** Client Portal API that serves the portal being signed in to. */
  apiUrl: string;
  /** Canonical portal callback returned by the API; safe to navigate to. */
  returnUrl: string;
  /** The portal's own anti-forgery value, handed back untouched. */
  state: string;
  /** Our anti-forgery value for the Google round trip. */
  googleNonce?: string;
  startedAt: number;
}

const STORAGE_KEY = 'redirectSignIn';

/** A sign-in name is at most a few hundred characters; anything longer is ignored. */
const MAX_LOGIN_HINT_LENGTH = 256;

/** A pending sign-in older than this is abandoned; the page then behaves as if it was never started. */
export const PENDING_MAX_AGE_MS = 10 * 60 * 1000;

export const normaliseProvider = (value: string | null | undefined): Provider | null => {
  switch (value?.toLowerCase()) {
    case 'microsoft':
      return 'Microsoft';
    case 'google':
      return 'Google';
    default:
      return null;
  }
};

/**
 * The Client Portal APIs that may own a portal address, in the order to ask them, from this app's own
 * configuration (`<base domain>=<api url>;...`). The address itself only selects among configured APIs;
 * it can never name one, so a crafted link can't make this page send a provider token anywhere else.
 *
 * An address under one of our base domains has exactly one owner. An address on the customer's own
 * domain matches none of them, so when a `*` catch-all is configured every configured API is a
 * candidate, the catch-all first: one auth app serves portals from more than one environment, and only
 * the API that holds the portal confirms its address.
 */
const CATCH_ALL = '*';

export const resolveClientPortalApiUrls = (
  returnUrl: string,
  config: string | undefined,
): string[] => {
  let host: string;
  try {
    host = new URL(returnUrl).hostname.toLowerCase();
  } catch {
    return [];
  }

  const entries = (config ?? '').split(';').flatMap(pair => {
    const separator = pair.indexOf('=');
    if (separator <= 0) return [];

    const domain = pair.slice(0, separator).trim().toLowerCase().replace(/^\./, '');
    const apiUrl = pair
      .slice(separator + 1)
      .trim()
      .replace(/\/+$/, '');
    return domain !== '' && apiUrl !== '' ? [{ domain, apiUrl }] : [];
  });

  const owner = entries.find(
    e => e.domain !== CATCH_ALL && (host === e.domain || host.endsWith(`.${e.domain}`)),
  );
  if (owner !== undefined) return [owner.apiUrl];

  if (!entries.some(e => e.domain === CATCH_ALL)) return [];

  const catchAllFirst = [
    ...entries.filter(e => e.domain === CATCH_ALL),
    ...entries.filter(e => e.domain !== CATCH_ALL),
  ];
  return Array.from(new Set(catchAllFirst.map(e => e.apiUrl)));
};

/** Asks each candidate in turn and keeps the first API that confirms the address as a portal callback. */
export const confirmReturnUrl = async (
  candidates: string[],
  returnUrl: string,
  validate: (apiUrl: string, returnUrl: string) => Promise<{ returnUrl: string }>,
): Promise<{ apiUrl: string; returnUrl: string } | null> => {
  for (const apiUrl of candidates) {
    const confirmed = await validate(apiUrl, returnUrl).then(
      result => result.returnUrl,
      () => null,
    );
    if (confirmed !== null) return { apiUrl, returnUrl: confirmed };
  }
  return null;
};

export const parseRedirectSignInRequest = (search: string): IRedirectSignInRequest | null => {
  const params = new URLSearchParams(search);
  const type = normaliseProvider(params.get('type'));
  const returnUrl = params.get('returnUrl');
  const state = params.get('state');
  if (!type || !returnUrl || !state) return null;

  const loginHint = params.get('loginHint');
  return loginHint && loginHint.length <= MAX_LOGIN_HINT_LENGTH
    ? { type, returnUrl, state, loginHint }
    : { type, returnUrl, state };
};

export const buildPortalReturnUrl = (
  canonicalReturnUrl: string,
  outcome: { code: string } | { error: SignInErrorKind },
  state: string,
): string => {
  // The fragment, not the query: a fragment is never sent to the portal's web server, so the single-use
  // code stays out of its access logs and out of Referer headers.
  const params = new URLSearchParams(
    'code' in outcome ? { code: outcome.code, state } : { error: outcome.error, state },
  );
  const url = new URL(canonicalReturnUrl);
  url.hash = params.toString();
  return url.toString();
};

/** Google's OAuth 2.0 redirect flow; the access token comes back in the URL fragment. */
export const buildGoogleAuthorizeUrl = (
  clientId: string,
  redirectUri: string,
  nonce: string,
): string => {
  const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('response_type', 'token');
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('include_granted_scopes', 'true');
  url.searchParams.set('state', nonce);
  return url.toString();
};

export const parseGoogleRedirect = (
  hash: string,
): { accessToken?: string; state?: string; error?: string } => {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  return {
    accessToken: params.get('access_token') ?? undefined,
    state: params.get('state') ?? undefined,
    error: params.get('error') ?? undefined,
  };
};

/** Maps a provider error code to what the portal shows: a silent return or a failure panel. */
export const toSignInErrorKind = (errorCode: string | undefined): SignInErrorKind =>
  errorCode === 'access_denied' || errorCode === 'user_cancelled' ? 'cancelled' : 'failed';

/** The address registered with the providers: this page without query or fragment. */
export const signInRedirectUri = (href: string): string => href.split('#')[0].split('?')[0].replace(/\/+$/, '');

const isPending = (value: unknown, now: number): value is IPendingRedirectSignIn => {
  const pending = value as Partial<IPendingRedirectSignIn> | null;
  return (
    !!pending &&
    normaliseProvider(pending.type) !== null &&
    typeof pending.apiUrl === 'string' &&
    typeof pending.returnUrl === 'string' &&
    typeof pending.state === 'string' &&
    typeof pending.startedAt === 'number' &&
    now - pending.startedAt >= 0 &&
    now - pending.startedAt <= PENDING_MAX_AGE_MS
  );
};

// sessionStorage can throw (private modes, storage partitioning), so every access is guarded.
export const savePendingSignIn = (pending: IPendingRedirectSignIn, storage?: Storage): boolean => {
  try {
    (storage ?? sessionStorage).setItem(STORAGE_KEY, JSON.stringify(pending));
    return true;
  } catch {
    return false;
  }
};

export const readPendingSignIn = (
  storage?: Storage,
  now: number = Date.now(),
): IPendingRedirectSignIn | null => {
  try {
    const raw = (storage ?? sessionStorage).getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isPending(parsed, now) ? parsed : null;
  } catch {
    return null;
  }
};

export const clearPendingSignIn = (storage?: Storage): void => {
  try {
    (storage ?? sessionStorage).removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
};

/**
 * True when this page was opened for, or is in the middle of, a same-tab portal sign-in. A visit that
 * carries the Pusher flow's `sessionId` (ConnectWise POD, portal inside Teams) always gets the Pusher
 * flow, even if an abandoned same-tab attempt left a pending entry in this tab.
 */
export const isRedirectSignIn = (search: string, storage?: Storage): boolean => {
  if (parseRedirectSignInRequest(search) !== null) return true;
  if (new URLSearchParams(search).has('sessionId')) return false;
  return readPendingSignIn(storage) !== null;
};
